import {
  SchoolReportService,
  InitEnvironmentVariable,
  UserService,
} from '@PikaDoApp/npm-pikado-logic';
import {ISchoolsRepository} from './ISchoolRepository';

import {School, SchoolsConnection} from '../../graphql/API';
import {school, schools} from './SchoolQueries';
import {generateClient, GraphQLResult} from 'aws-amplify/api';
import {getEnvironment} from '../../lib/getEnvironment';
import {mapSchoolLogoToPromo} from '../../lib/mapSchoolLogoToPromo';

const client = generateClient();
const userService = new UserService();

const getEntityId = (entity: any): string | undefined =>
  entity?.id ?? entity?._id;

const getClassSchoolId = (student: any): string | undefined =>
  student?.classSchoolId ?? student?.classschool?.id ?? student?.classschool?._id;

const mapStudentForClass = (student: any) => ({
  id: getEntityId(student),
  name: student.name,
  age: student.age,
  birthDate: student.birthDate,
  gender: student.gender,
  user: {
    id: student.userId,
    country: student.user?.country || 'SA',
  },
  statistic: {
    id: getEntityId(student),
    averageScore: 0,
  },
  __typename: 'Student',
});

export class SchoolRepository implements ISchoolsRepository {
  private schoolReportService: SchoolReportService;

  constructor() {
    console.log('Init Environment Variable', getEnvironment());
    InitEnvironmentVariable(getEnvironment());
    this.schoolReportService = new SchoolReportService();
  }

  async getSchools(
    nextToken: string,
    searchValue?: string,
  ): Promise<SchoolsConnection> {
    try {
      const filter = {name: {contains: searchValue}};

      const response: any = await client.graphql({
        query: schools,
        variables: {
          nextToken,
          filter,
          ...(searchValue?.length > 0 ? null : {scannedCount: 101}),
        },
      });

      return response.data?.schools;
    } catch (e) {
      console.log('response Error->', e);
    }
  }

  async getSchoolById(schoolId: string): Promise<any> {
    try {
      console.log('Fetching school profile from Pikado logic, schoolId:', schoolId);
      const schoolProfile = await userService.getSchoolById(schoolId);
      
      console.log('Fetching classes for schoolId:', schoolId);
      const classesResponse = await userService.getClassSchoolsBySchoolId(schoolId);
      
      console.log('Fetching students for schoolId:', schoolId);
      let studentsResponse = [];
      try {
        studentsResponse = await userService.getStudentsBySchoolId(schoolId);
      } catch (studentsErr) {
        console.error('Error fetching students:', studentsErr);
      }

      // Map everything to the structure expected by the React frontend
      const mappedClasses = classesResponse.map((classItem: any) => {
        const classId = getEntityId(classItem);
        const classStudents = studentsResponse
          .filter((s: any) => getClassSchoolId(s) === classId)
          .map(mapStudentForClass);

        return {
          id: classId,
          name: classItem.name,
          nameArabic: classItem.nameArabic,
          level: classItem.levelId ? {
            id: classItem.levelId,
            number: 1,
            __typename: 'Level',
          } : null,
          statistic: {
            id: classItem._id,
            averageScore: 0,
            __typename: 'Classstatistic',
          },
          students: classStudents,
          teacherPrograms: classItem.teacherPrograms || [],
          __typename: 'Classschool',
        };
      });

      const schoolData = {
        id: schoolProfile._id,
        name: schoolProfile.name,
        nameArabic: schoolProfile.nameArabic,
        classesschool: mappedClasses,
        students: mappedClasses.flatMap((classItem: any) => classItem.students || []),
        createdAt: schoolProfile.createdAt,
        updatedAt: schoolProfile.updatedAt,
        promo: mapSchoolLogoToPromo(schoolProfile.logoUrl),
        __typename: 'School',
      };

      console.log('Mapped schoolData with students:', JSON.stringify(schoolData, null, 2));
      return schoolData;
    } catch (error) {
      console.log('getSchoolById Error: ', error);
      throw error;
    }
  }

  async getClassExeForPrevSecDays(classId: string): Promise<School> {
    try {
      const response: any =
        await this.schoolReportService.getExercisesForFirstStudent(classId);
      return response.exercises || [];
    } catch (error) {
      console.log('getClassExeForPrevSecDays Error: ', error);
    }
  }
}
